import React from 'react';
import { Row, Col, Card, Table, Tag, Typography, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Campaigns.css';

const { Title, Text } = Typography;

const WhiteLabelCampaigns = () => {
  const navigate = useNavigate();

  // Mock campaign data across all clients
  const campaigns = [
    { id: 1, name: 'Service Reminder SMS - ProShine', client: 'ProShine Auto Detailing', type: 'SMS', impressions: 7786, clicks: 399, ctr: '5.13%', status: 'completed' },
    { id: 2, name: 'Seasonal Maintenance Tips - ProShine', client: 'ProShine Auto Detailing', type: 'EMAIL', impressions: 9299, clicks: 504, ctr: '5.43%', status: 'completed' },
    { id: 3, name: 'Brand Building - ProShine', client: 'ProShine Auto Detailing', type: 'META', impressions: 0, clicks: 0, ctr: '0.00%', status: 'draft' },
    { id: 4, name: 'Competitor Keywords - ProShine', client: 'ProShine Auto Detailing', type: 'GOOGLE', impressions: 14409, clicks: 714, ctr: '4.96%', status: 'paused' },
    { id: 5, name: 'Emergency Service Alert - ProShine', client: 'ProShine Auto Detailing', type: 'SMS', impressions: 5806, clicks: 244, ctr: '4.17%', status: 'active' },
    { id: 6, name: 'Service Reminder SMS - Blue', client: 'Blue Ridge HVAC', type: 'SMS', impressions: 8308, clicks: 270, ctr: '3.20%', status: 'active' },
    { id: 7, name: 'Seasonal Maintenance Tips - Blue', client: 'Blue Ridge HVAC', type: 'EMAIL', impressions: 3910, clicks: 236, ctr: '6.00%', status: 'active' },
    { id: 8, name: 'Brand Building - Blue', client: 'Blue Ridge HVAC', type: 'META', impressions: 31741, clicks: 935, ctr: '2.95%', status: 'active' },
    { id: 9, name: 'Competitor Keywords - Blue', client: 'Blue Ridge HVAC', type: 'GOOGLE', impressions: 0, clicks: 0, ctr: '0.00%', status: 'draft' }
  ];

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: 'blue', text: 'completed' },
      active: { color: 'green', text: 'active' },
      paused: { color: 'orange', text: 'paused' },
      draft: { color: 'default', text: 'draft' }
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong style={{ color: '#1e40af' }}>{text}</Text>
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      render: (text) => <Text strong style={{ color: '#1e40af' }}>{text}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeConfig = {
          SMS: { color: 'blue', text: 'SMS' },
          EMAIL: { color: 'orange', text: 'EMAIL' },
          META: { color: 'purple', text: 'META' },
          GOOGLE: { color: 'red', text: 'GOOGLE' }
        };
        const config = typeConfig[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Impressions',
      dataIndex: 'impressions',
      key: 'impressions',
      align: 'right',
      render: (val) => val.toLocaleString()
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      align: 'right'
    },
    {
      title: 'CTR',
      dataIndex: 'ctr',
      key: 'ctr',
      align: 'right'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: getStatusTag
    }
  ];

  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);

  return (
    <div className="reseller-campaigns">
      {/* Header */}
      <div className="campaigns-header">
        <div>
          <Title level={2} style={{ margin: 0 }}>Campaign Overview</Title>
          <Text type="secondary">ServiceTech Marketing Partners</Text>
        </div>
        <div className="last-updated">Last updated: 7:28:54 PM</div>
      </div>

      {/* Page Title */}
      <div className="page-title-section">
        <Title level={3} style={{ margin: 0 }}>Campaign Overview</Title>
        <Text type="secondary">Performance across all managed clients</Text>
      </div>

      {/* Stats */}
      <Row gutter={16} className="campaign-stats">
        <Col span={6}>
          <Card className="stat-card-small">
            <div>
              <Text type="secondary">Total Campaigns</Text>
              <div className="stat-value-large">{totalCampaigns}</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <div>
              <Text type="secondary">Active</Text>
              <div className="stat-value-large">{activeCampaigns}</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <div>
              <Text type="secondary">Total Impressions</Text>
              <div className="stat-value-large">{totalImpressions.toLocaleString()}</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <div>
              <Text type="secondary">Total Clicks</Text>
              <div className="stat-value-large">{totalClicks.toLocaleString()}</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* All Campaigns Table */}
      <Card className="campaigns-table-card">
        <div className="table-header">
          <div>
            <Title level={4} style={{ margin: 0 }}>All Campaigns</Title>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/whitelabel/campaigns/create')}
          >
            Create Campaign
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default WhiteLabelCampaigns;
